
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import BigAdd from "./BigAdd";
import ButtonTags from "./ButtonTags";
import Business3 from "./Business3";
import DOMPurify from "dompurify";
import { faFacebook, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { faLink } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import BollywoodNews2 from "./BollywoodNews2";

const BASE_URL = "https://api.shabdtoday.com/";
const online = "https://api.shabdtoday.com/api/home/getAllDetails/";
const local = "https://localhost:44351/api/home/getAllDetails/";
const clickOnline = "https://api.shabdtoday.com/api/home/update-viewcount";

function CommonNewsDetailsPage() {
  const { mainKhabarId } = useParams();
  const [newsDetails, setNewsDetails] = useState(null);
  const [related, setRelated] = useState([]);
  const [topNews, setTopNews] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMain, setLoadingMain] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const observer = useRef();
  const loadedIdsRef = useRef(new Set());

  useEffect(() => {
    if (newsDetails?.mainTazaKhabars) {
      loadedIdsRef.current.add(newsDetails.mainTazaKhabars.mainKhabarId);
      (related || []).forEach(n => loadedIdsRef.current.add(n.mainKhabarId));
    }
  }, [newsDetails]);


  const renderHtmlContent = (html) => ({
    __html: DOMPurify.sanitize(html || ""),
  });

  const truncateText = (text, maxLength) => {
    if (!text) return "";
    if (text.length > maxLength) return text.substring(0, maxLength) + "...";
    return text;
  };

  const stripHtmlTags = (html, maxLength) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html || "";
    const strippedText = tempDiv.textContent || tempDiv.innerText || "";
    return truncateText(strippedText, maxLength);
  };

  const getTimeAgoInHindi = (dateString) => {
    if (!dateString) return "";
    const now = new Date();
    const past = new Date(dateString);
    const diffInSeconds = Math.floor((now - past) / 1000);
    if (diffInSeconds < 60) return `${diffInSeconds} सेकंड पहले`;
    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `${diffInMinutes} मिनट पहले`;
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} घंटे पहले`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) return `${diffInDays} दिन पहले`;
    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) return `${diffInMonths} महीने पहले`;
    const diffInYears = Math.floor(diffInMonths / 12);
    return `${diffInYears} साल पहले`;
  };

  useEffect(() => {
    if (!mainKhabarId) return;
    setLoadingMain(true);
    setRelated([]);
    setTopNews([]);
    setPage(1);
    setHasMore(true);
    const fetch = async () => {
      try {
        const res = await axios.get(`${online}${encodeURIComponent(mainKhabarId)}?page=1&limit=${limit}`);
        const data = res.data;

        setNewsDetails(data);

        if (data.topNews && Array.isArray(data.topNews)) setTopNews(data.topNews);

        const initialRelated = (data.relatedNews || []).filter(
          (r) => r.mainKhabarId !== data.mainTazaKhabars?.mainKhabarId
        );
        setRelated(dedupeById(initialRelated));
        console.log("Related News (using allNews):", data.relatedNews?.length);

        if (!initialRelated || initialRelated.length < limit) setHasMore(false);
      } catch (err) {
        console.error("Error fetching details:", err);
      } finally {
        setLoadingMain(false);
      }
    };

    fetch();
  }, [mainKhabarId]);

  const dedupeById = (arr) => {
    const map = new Map();
    (arr || []).forEach((it) => {
      if (it && it.mainKhabarId != null) map.set(String(it.mainKhabarId), it);
    });
    return Array.from(map.values());
  };

  const loadMoreRelated = useCallback(async () => {
    if (!newsDetails?.mainTazaKhabars || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const nextPage = page + 1;

    try {
      const res = await axios.get(`${online}${encodeURIComponent(mainKhabarId)}?page=${nextPage}&limit=${limit}`);
      const data = res.data;

      const newRelatedRaw = data.relatedNews || data.allNews || [];
      const newRelatedFiltered = (newRelatedRaw || []).filter(
        r => r.mainKhabarId != null && r.mainKhabarId !== newsDetails.mainTazaKhabars.mainKhabarId
      );

      if (newRelatedFiltered.length === 0) {
        setHasMore(false);
      } else {
        setRelated(prev => dedupeById([...prev, ...newRelatedFiltered]));
        setPage(nextPage);
      }
    } catch (err) {
      console.error("Error loading more related:", err);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [newsDetails, hasMore, loadingMore, page, mainKhabarId]);


  useEffect(() => {
    observer.current = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMoreRelated();
    });

    const target = document.getElementById("load-more-related");
    if (target) observer.current.observe(target);

    return () => observer.current && observer.current.disconnect();
  }, [loadMoreRelated]);

  const handleRelatedClick = async (slug, mainKhabarId_) => {
    try {
      axios.post(clickOnline, { mainKhabarId: mainKhabarId_ }).catch(() => { });
      window.location.href = `/News-Details/${slug}`;
    } catch (err) {
      console.error(err);
      window.location.href = `/News-Details/${slug}`;
    }
  };

  const handleCopyLink = async () => {
    try {
      const url = `http://api.shabdtoday.com/Share/Page/${mainKhabarId}`;
      await navigator.clipboard.writeText(url);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 3000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  if (loadingMain) {
    return <div style={{ minHeight: 200 }} />;
  }

  if (!newsDetails || !newsDetails.mainTazaKhabars) {
    return <div>News not found.</div>;
  }

  const main = newsDetails.mainTazaKhabars;

  return (
    <>
      {copySuccess && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: "#28a745",
            color: "#fff",
            padding: "10px 20px",
            borderRadius: "8px",
            fontSize: "16px",
            zIndex: 9999,
            boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
          }}
        >
          ✅ लिंक कॉपी हो गया है
        </div>
      )}

      <div className="ak-post-wrapper" style={{ transform: "none" }}>
        <div className="ak-content-wrap ak-post-wrap ak-layout-style-1 clearfix ak-post-style-7 clearfix" style={{ transform: "none" }}>
          <div className="ak-container" style={{ transform: "none" }}>
            <div className="ak-content" style={{ transform: "none" }}>
              <div className="containercustome" style={{ transform: "none" }}>
                <div className="mt-2">
                  <BigAdd />
                </div>

                <div className="row grid-container" style={{ transform: "none" }}>
                  <div className="grid-item ak_column_1 col-md-3 sidebar-column sidebar-column-primary sticky-sidebar" style={{ position: "relative", overflow: "visible", boxSizing: "border-box", minHeight: 1 }}>
                    <div className="theiaStickySidebar" style={{ paddingTop: 0, paddingBottom: 1, position: "sticky", transform: "none", top: 0 }}>
                      <div className="sidebar post-sidebar">
                        <Business3 />
                        {/* <SocialMediaShare /> */}
                      </div>
                    </div>
                  </div>

                  <div className="grid-item ak_column_2 col-md-6">
                    <header className="entry-header ak-post-header mb-2">
                      <span style={{ fontWeight: 600, fontSize: "25px" }} dangerouslySetInnerHTML={renderHtmlContent(main.newsTag)} />
                    </header>

                    <div style={{ marginBottom: "2px" }}>
                      {main.editor && stripHtmlTags(main.editor, 100)} |
                      <span style={{ color: "grey" }}> {getTimeAgoInHindi(main.date)}</span>
                    </div>

                    <div className="ak-article-inner">
                      <div className="ak-post-featured">
                        <div className="ak-featured-cover">
                          <div className="ak-featured-thumb size-auto" style={{ paddingBottom: "70%" }}>
                            <img
                              width={750}
                              height={275}
                              src={BASE_URL + main.newsImage}
                              alt={stripHtmlTags(main.newsTag, 120)}
                              sizes="750px"
                            />
                          </div>
                        </div>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0 10px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "grey" }}>
                          <FontAwesomeIcon icon={faEye} style={{ fontSize: 14 }} />
                          <span style={{ fontSize: 14 }}>{main.viewCount ?? 0}</span>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <Link to="#" onClick={(e) => { e.preventDefault(); openPopup(generateShareLinks(main.slug, main.newsTag).facebook); }} aria-label="Share on Facebook">
                            <FontAwesomeIcon icon={faFacebook} style={{ color: "#1877F2", fontSize: 24 }} />
                          </Link>

                          <Link to="#" onClick={(e) => { e.preventDefault(); openPopup(generateShareLinks(main.mainKhabarId, main.newsTag).twitter); }} aria-label="Share on Twitter">
                            <FontAwesomeIcon icon={faXTwitter} style={{ color: "#0088cc", fontSize: 24 }} />
                          </Link>

                          <Link to="#" onClick={(e) => { e.preventDefault(); handleCopyLink(); }}>
                            <FontAwesomeIcon icon={faLink} style={{ color: "#28a745", fontSize: 24, cursor: "pointer" }} />
                          </Link>
                        </div>
                      </div>

                      <br />

                      <div className="ak-post-content">
                        <span dangerouslySetInnerHTML={renderHtmlContent(main.newsDetails)}></span>
                      </div>

                      <ButtonTags />

                      {/* RELATED NEWS STRIP (uniform small cards - no custom CSS file) */}
                      <div style={{ marginTop: 28 }}>
                        <h4 style={{ marginBottom: 12 }}>इस श्रेणी की अन्य खबरें</h4>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                          {related && related.length > 0 ? (
                            related.map((item) => (
                              <article
                                key={item.mainKhabarId}
                                style={{
                                  width: "calc(50% - 12px)",
                                  border: "1px solid #e6e6e6",
                                  borderRadius: 6,
                                  overflow: "hidden",
                                  textDecoration: "none",
                                }}
                              >
                                <Link
                                  to="#"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleRelatedClick(item.slug, item.mainKhabarId);
                                  }}
                                  style={{ color: "inherit", textDecoration: "none" }}
                                >
                                  <div style={{ width: "100%", height: 120, overflow: "hidden" }}>
                                    <img
                                      src={BASE_URL + item.newsImage}
                                      alt={stripHtmlTags(item.newsTag, 100)}
                                      style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                    />
                                  </div>
                                  <div style={{ padding: "8px 10px" }}>
                                    {/* <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.3 }}>
                                      {stripHtmlTags(item.newsTag, 120)}
                                    </div> */}
                                    <span style={{ fontWeight: 600, fontSize: "14px",lineHeight: 1.3 }} dangerouslySetInnerHTML={renderHtmlContent(item.newsTag)} />
                                    <div style={{ color: "#777", fontSize: 12, marginTop: 6 }}>
                                      {getTimeAgoInHindi(item.date)}
                                    </div>
                                  </div>
                                </Link>
                              </article>
                            ))
                          ) : (
                            <div>No related news found.</div>
                          )}
                        </div>

                        <div id="load-more-related" style={{ textAlign: "center", padding: 18 }}>
                          {loadingMore && <div>Loading more...</div>}
                          {!hasMore && <div>No more news.</div>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="grid-item ak_column_1 col-md-3 sidebar-column sidebar-column-primary-right sticky-sidebar" style={{ position: "relative", overflow: "visible", boxSizing: "border-box", minHeight: 1 }}>
                    <div className="stickybar" style={{ paddingTop: 0, paddingBottom: 1, position: "sticky", transform: "none", top: 0 }}>
                      <div className="sidebar post-sidebar">
                        <BollywoodNews2 />
                        {/* <InternationalDetails2 /> */}
                      </div>
                    </div>
                  </div>
                </div>
                {/* end row */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  function openPopup(url) {
    const w = 600, h = 500;
    const left = window.innerWidth / 2 - w / 2;
    const top = window.innerHeight / 2 - h / 2;
    window.open(url, "_blank", `width=${w},height=${h},top=${top},left=${left},status=no,toolbar=no,menubar=no,location=no`);
  }

  function generateShareLinks(slugOrId, heading) {
    const encodedHeading = encodeURIComponent(heading || "");
    const shareUrl = `https://api.shabdtoday.com/Share/Page2/${slugOrId}`;
    return {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodedHeading}`,
    };
  }
}

export default CommonNewsDetailsPage;
